<template>
  <div v-show="isVisible" class="region-targeting-dummy">
    <div class="region-targeting-header"></div>
    <div class="region-targeting-title">
      <span class="region-targeting-required">Locations<span class="required-mark" aria-hidden="true">*</span></span>
    </div>
    <div class="region-targeting-card" ref="card">
      <div class="region-targeting-map-controls">
        <button
          type="button"
          class="region-targeting-collapse-toggle"
          data-bs-toggle="collapse"
          :data-bs-target="`#${mapCollapseId}`"
          :aria-controls="mapCollapseId"
          @click="toggleMapCollapse"
        >
          <span> {{ isMapCollapsed ? 'Show Map' : 'Hide Map' }} </span>
          <i class="fa-solid" :class="isMapCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
        </button>
      </div>
      <div class="region-targeting-results" ref="results">
        <div
          v-for="row in currentRegion.include"
          :key="`include-${row.id}`"
          class="region-targeting-row"
        >
          <div
            class="region-targeting-item include"
            :class="{ 'is-selected': activeRowId === row.id }"
            @click="row.locationType !== 'city' && row.locationType !== 'country' ? openRadiusPopover(row, $event) : null"
          >
          <span class="region-targeting-pill include">
            <i class="fa-solid fa-check"></i>
          </span>
          <span class="region-targeting-text">
            {{ formatRowLabel(row) }}
            <span v-if="formatRowMeta(row)" class="region-targeting-meta">
              {{ formatRowMeta(row) }}
            </span>
          </span>
          <button
            v-if="row.locationType !== 'city' && row.locationType !== 'country'"
            type="button"
            class="region-targeting-radius"
            data-radius-trigger
            :data-row-id="row.id"
            @click.stop="openRadiusPopover(row, $event)"
          >
            + {{ row.radius }}mi <i class="fa-solid fa-caret-down"></i>
          </button>
          </div>
          <button
            type="button"
            class="region-targeting-remove"
            @click.stop="requestRemoveRow(row, 'include')"
          >
            <i class="fa-solid fa-trash-can"></i>
          </button>
        </div>
        <div
          v-for="row in currentRegion.exclude"
          :key="`exclude-${row.id}`"
          class="region-targeting-row"
        >
          <div
            class="region-targeting-item exclude"
            :class="{ 'is-selected': activeRowId === row.id }"
            @click="row.locationType !== 'city' && row.locationType !== 'country' ? openRadiusPopover(row, $event) : null"
          >
          <span class="region-targeting-pill exclude"> x </span>
          <span class="region-targeting-text">
            {{ formatRowLabel(row) }}
            <span v-if="formatRowMeta(row)" class="region-targeting-meta">
              {{ formatRowMeta(row) }}
            </span>
          </span>
          <button
            v-if="row.locationType !== 'city' && row.locationType !== 'country'"
            type="button"
            class="region-targeting-radius"
            data-radius-trigger
            :data-row-id="row.id"
            @click.stop="openRadiusPopover(row, $event)"
          >
            + {{ row.radius }}mi <i class="fa-solid fa-caret-down"></i>
          </button>
          </div>
          <button
            type="button"
            class="region-targeting-remove"
            @click.stop="requestRemoveRow(row, 'exclude')"
          >
            <i class="fa-solid fa-trash-can"></i>
          </button>
        </div>
      </div>
      <div class="region-targeting-radius-popover d-none" ref="popover">
        <div class="region-targeting-radius-title"> Current city only </div>
        <div class="region-targeting-radius-option active">
          <i class="fa-solid fa-check"></i>
          Cities within radius
        </div>
        <div class="region-targeting-radius-slider">
          <span>10</span>
          <input
            type="range"
            min="10"
            max="50"
            :value="activeRow ? activeRow.radius : 10"
            @input="updateRadiusFromSlider"
          />
          <span>50</span>
          <div class="region-targeting-radius-value">
            <input
              type="text"
              :value="activeRow ? activeRow.radius : 10"
              @input="updateRadiusFromInput"
            />
            <span>mi</span>
          </div>
        </div>
      </div>
      <div class="region-targeting-search">
        <input type="hidden" :id="typeId" :name="typeName" :value="currentType" />
        <div
          class="region-targeting-type"
          :class="{ 'is-open': isTypeOpen, 'is-include': currentType === 'include' }"
        >
          <button type="button" class="region-targeting-type-toggle" @click="toggleTypeMenu">
            <span class="region-targeting-type-icon" :class="currentType">
              <i v-if="currentType === 'include'" class="fa-solid fa-check"></i>
              <i v-else class="fa-solid fa-xmark"></i>
            </span>
            <span class="region-targeting-type-label">
              {{ currentType === 'include' ? 'Include' : 'Exclude' }}
            </span>
            <i class="fa-solid fa-caret-down region-targeting-type-caret"></i>
          </button>
          <div v-if="isTypeOpen" class="region-targeting-type-menu">
            <button type="button" class="region-targeting-type-option" @click="setType('include')">
              <span class="region-targeting-type-icon include">
                <i class="fa-solid fa-check"></i>
              </span>
              <span>Include</span>
            </button>
            <button type="button" class="region-targeting-type-option" @click="setType('exclude')">
              <span class="region-targeting-type-icon exclude">
                <i class="fa-solid fa-xmark"></i>
              </span>
              <span>Exclude</span>
            </button>
          </div>
        </div>
        <div class="region-targeting-search-input">
          <i class="fa-solid fa-magnifying-glass"></i>
          <input
            ref="searchInput"
            :id="queryId"
            :name="queryName"
            type="text"
            placeholder="Search location"
            v-model="searchQuery"
            @input="handleSearchInput"
            @focus="isSearchFocused = true"
            @blur="handleSearchBlur"
          />
        </div>
        <div v-if="showSearchResults" class="region-targeting-search-results">
          <button
            v-for="result in searchResults"
            :key="result.properties?.osm_id || result.properties?.name"
            type="button"
            class="region-targeting-search-result"
            @mousedown.prevent="selectSearchResult(result)"
          >
            <span class="region-targeting-search-label">
              {{ formatSearchLabel(result) }}
            </span>
            <span v-if="formatSearchMeta(result)" class="region-targeting-search-meta">
              {{ formatSearchMeta(result) }}
            </span>
          </button>
          <div v-if="isSearching" class="region-targeting-search-loading">Searching...</div>
          <div v-if="!isSearching && searchResults.length === 0" class="region-targeting-search-empty">
            No results
          </div>
        </div>
      </div>
      <div :id="mapCollapseId" class="collapse show">
        <div class="region-targeting-map">
          <div ref="mapContainer" class="region-targeting-map-canvas"></div>
          <button type="button" class="region-targeting-drop-pin" @click="toggleDropPin">
            <i class="fa-solid fa-location-dot"></i>
            {{ dropPinMode ? 'Click On Map' : 'Drop Pin' }}
          </button>
        </div>
      </div>
    </div>
    <button
      type="button"
      class="region-targeting-map-link"
      data-bs-toggle="modal"
      :data-bs-target="`#${bulkModalId}`"
    >
      Add locations in bulk
    </button>
    <input type="hidden" :name="payloadName" :id="payloadId" :value="serializedPayload" />

    <div
      class="modal fade"
      :id="bulkModalId"
      tabindex="-1"
      :aria-labelledby="bulkModalLabelId"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content region-targeting-modal">
          <div class="modal-header">
            <h5 class="modal-title" :id="bulkModalLabelId">Add locations in bulk</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p class="region-targeting-modal-subtitle">
              Select your location type
            </p>
            <div class="region-targeting-modal-row">
              <label class="region-targeting-modal-label" :for="bulkTypeId">Location type:</label>
              <select
                :id="bulkTypeId"
                class="form-select region-targeting-modal-select"
                v-model="bulkType"
              >
                <option value="" disabled>Select</option>
                <option value="countries">Countries</option>
                <option value="cities">Cities</option>
                <option value="postal_codes">Postal codes</option>
              </select>
            </div>
            <div v-if="bulkRequiresCountry" class="region-targeting-modal-row">
              <label class="region-targeting-modal-label" :for="bulkCountryId">Country:</label>
              <select
                :id="bulkCountryId"
                class="form-select region-targeting-modal-select"
                v-model="bulkCountry"
              >
                <option value="" disabled>Select country</option>
                <option v-for="country in countries" :key="country.id" :value="country.id">
                  {{ country.name }}
                </option>
              </select>
            </div>

            <div v-if="bulkType" class="region-targeting-modal-row region-targeting-modal-textarea">
              <label class="region-targeting-modal-label" :for="bulkTextId">Add locations</label>
              <textarea
                :id="bulkTextId"
                class="form-control"
                rows="6"
                :placeholder="bulkPlaceholder"
                v-model="bulkText"
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!bulkCanSubmit"
              @click="handleBulkMatch"
            >
              {{ bulkIsProcessing ? 'Adding...' : 'Add locations' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal fade"
      :id="deleteModalId"
      tabindex="-1"
      :aria-labelledby="deleteModalLabelId"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content region-targeting-modal">
          <div class="modal-header">
            <h5 class="modal-title" :id="deleteModalLabelId">Remove location</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <p>Are you sure you want to remove this location?</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-danger" @click="confirmRemoveRow">
              Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import L from 'leaflet'
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

export default {
  name: 'RegionLocations',
  props: {
    listId: {
      type: String,
      required: false,
      default: ''
    },
    toggleAllId: {
      type: String,
      required: true
    },
    toggleSpecificId: {
      type: String,
      required: true
    },
    typeId: {
      type: String,
      required: true
    },
    typeName: {
      type: String,
      required: true
    },
    queryId: {
      type: String,
      required: true
    },
    queryName: {
      type: String,
      required: true
    },
    payloadId: {
      type: String,
      required: true
    },
    payloadName: {
      type: String,
      required: true
    },
    countries: {
      type: Array,
      required: false,
      default: () => []
    },
    existingLocations: {
      type: Array,
      required: false,
      default: () => []
    },
    geoapifyKey: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      isVisible: false,
      regionsData: {
        include: [],
        exclude: []
      },
      activeRowId: null,
      searchQuery: '',
      searchResults: [],
      isSearching: false,
      isSearchFocused: false,
      dropPinMode: false,
      map: null,
      markers: [],
      markersById: {},
      circles: {},
      nextRowId: 1,
      searchDebounce: null,
      currentType: 'include',
      isMapCollapsed: false,
      bulkType: '',
      bulkCountry: '',
      bulkText: '',
      bulkIsProcessing: false,
      pendingRemove: null,
      documentClickHandler: null,
      suppressOutsideClose: false,
      isTypeOpen: false,
      geoapifyRequestCount: 0
    }
  },
  computed: {
    currentRegion() {
      return this.regionsData
    },
    activeRow() {
      if (!this.activeRowId) {
        return null
      }

      const matchInclude = this.currentRegion.include.find((row) => row.id === this.activeRowId)
      if (matchInclude) {
        return matchInclude
      }

      return this.currentRegion.exclude.find((row) => row.id === this.activeRowId) || null
    },
    serializedPayload() {
      return JSON.stringify(this.regionsData)
    },
    includeSelectClass() {
      return this.currentType === 'include' ? 'is-include' : ''
    },
    showSearchResults() {
      return this.isSearchFocused && (this.searchResults.length > 0 || this.isSearching)
    },
    mapCollapseId() {
      return `${this.payloadId}-map-collapse`
    },
    bulkModalId() {
      return `${this.payloadId}-bulk-modal`
    },
    bulkModalLabelId() {
      return `${this.payloadId}-bulk-modal-label`
    },
    bulkTypeId() {
      return `${this.payloadId}-bulk-type`
    },
    bulkCountryId() {
      return `${this.payloadId}-bulk-country`
    },
    bulkTextId() {
      return `${this.payloadId}-bulk-text`
    },
    deleteModalId() {
      return `${this.payloadId}-delete-modal`
    },
    deleteModalLabelId() {
      return `${this.payloadId}-delete-modal-label`
    },
    bulkRequiresCountry() {
      return this.bulkType === 'cities' || this.bulkType === 'postal_codes'
    },
    bulkCanSubmit() {
      if (!this.bulkText.trim() || this.bulkIsProcessing) {
        return false
      }
      if (this.bulkRequiresCountry && !this.bulkCountry) {
        return false
      }
      return true
    },
    bulkCountryRecord() {
      if (!this.bulkCountry) {
        return null
      }
      return this.countries.find((country) => country.id === this.bulkCountry) || null
    },
    bulkPlaceholder() {
      if (this.bulkType === 'countries') {
        return 'Examples: United States, Canada, Mexico'
      }
      if (this.bulkType === 'cities') {
        return 'Examples: New York, NY; Los Angeles, CA'
      }
      if (this.bulkType === 'postal_codes') {
        return 'Examples: 94015, 94014, 90007 (requires country)'
      }
      return ''
    }
  },
  watch: {
    isVisible(value) {
      if (value && this.map) {
        this.$nextTick(() => {
          this.map.invalidateSize()
        })
      }
    },
    isMapCollapsed(value) {
      if (!value && this.map) {
        this.$nextTick(() => {
          this.map.invalidateSize()
        })
      }
    },
    activeRowId(newId, oldId) {
      if (oldId) {
        this.updateRowStyles(oldId, false)
      }
      if (newId) {
        this.updateRowStyles(newId, true)
      }
    }
  },
  mounted() {
    // console.log('RegionLocations countries:', this.countries)
    this.wireRegionToggles()
    this.wireRadiusPopover()
    this.initMap()
    this.loadExistingLocations()
    this.syncTypeFromSelect()
  },
  beforeUnmount() {
    if (this.documentClickHandler) {
      document.removeEventListener('click', this.documentClickHandler)
      this.documentClickHandler = null
    }
    if (this.searchDebounce) {
      clearTimeout(this.searchDebounce)
      this.searchDebounce = null
    }
  },
  methods: {
    // Sync include/exclude selection from the underlying select DOM value.
    syncTypeFromSelect() {
      const select = this.$refs.typeSelect
      if (select && (select.value === 'include' || select.value === 'exclude')) {
        this.currentType = select.value
      }
    },
    // Load saved locations (edit mode), render markers/circles, then fit the map.
    loadExistingLocations() {
      if (!this.existingLocations || this.existingLocations.length === 0) {
        return
      }

      // console.info('[RegionLocations] existing locations (edit mode)', this.existingLocations)
      // console.log('Existing locations (raw):', this.existingLocations)
      this.existingLocations.forEach((loc) => {
        const mode = loc.mode || 'include'
        const row = {
          id: this.nextRowId++,
          label: loc.label || '',
          radius: loc.radius || 10,
          lat: parseFloat(loc.lat),
          lng: parseFloat(loc.lng),
          street: loc.street || null,
          house_number: loc.house_number || null,
          country: loc.country || null,
          city: loc.city || null,
          state: loc.state || null,
          region: loc.region || null,
          county: loc.county || null,
          district: loc.district || null,
          postcode: loc.postcode || null,
          bbox_min_lon: loc.bbox_min_lon != null ? parseFloat(loc.bbox_min_lon) : null,
          bbox_min_lat: loc.bbox_min_lat != null ? parseFloat(loc.bbox_min_lat) : null,
          bbox_max_lon: loc.bbox_max_lon != null ? parseFloat(loc.bbox_max_lon) : null,
          bbox_max_lat: loc.bbox_max_lat != null ? parseFloat(loc.bbox_max_lat) : null,
          source: loc.source || 'search'
        }
        this.regionsData[mode].push(row)
        this.addMarker({ lat: row.lat, lng: row.lng }, row.id, mode)
        this.addRadiusCircle(row, mode, { fitBounds: false })
      })

      const allPoints = [...this.regionsData.include, ...this.regionsData.exclude]
      if (allPoints.length > 0 && this.map) {
        if (allPoints.length === 1) {
          const single = allPoints[0]
          const circle = this.circles[single.id]
          if (circle) {
            this.map.fitBounds(circle.getBounds(), { padding: [40, 40], maxZoom: 8 })
          } else {
            this.map.setView([single.lat, single.lng], 8)
          }
          return
        }
        const first = allPoints[0]
        this.map.setView([first.lat, first.lng], 8)
      }
    },
    // Update include/exclude type from a change event (currently unused).
    handleTypeChange(event) {
      this.currentType = event.target.value
      event.target.blur()
    },
    // Wire the All/Specific radios to show/hide the component and container.
    wireRegionToggles() {
      const toggleAll = document.getElementById(this.toggleAllId)
      const toggleSpecific = document.getElementById(this.toggleSpecificId)
      const listContainer = document.getElementById(this.listId)

      const syncVisibility = () => {
        if (toggleSpecific && toggleSpecific.checked) {
          if (listContainer) {
            listContainer.classList.remove('d-none')
          }
          this.isVisible = true
          return
        }

        if (listContainer) {
          listContainer.classList.add('d-none')
        }
        this.isVisible = false
        this.closePopover()
      }

      if (toggleAll) {
        toggleAll.addEventListener('change', syncVisibility)
        toggleAll.addEventListener('click', syncVisibility)
      }

      if (toggleSpecific) {
        toggleSpecific.addEventListener('change', syncVisibility)
        toggleSpecific.addEventListener('click', syncVisibility)
      }

      // Initialize visibility from current radio state.
      syncVisibility()
    },
    // Toggle include/exclude dropdown.
    toggleTypeMenu() {
      this.isTypeOpen = !this.isTypeOpen
    },
    // Set include/exclude and close dropdown.
    setType(value) {
      if (value !== 'include' && value !== 'exclude') {
        return
      }
      this.currentType = value
      this.isTypeOpen = false
    },
    // Register a document-level click handler to close the radius popover.
    wireRadiusPopover() {
      this.documentClickHandler = (event) => {
        if (this.suppressOutsideClose) {
          this.suppressOutsideClose = false
          return
        }
        if (this.isTypeOpen) {
          const toggle = this.$el?.querySelector('.region-targeting-type')
          if (toggle && toggle.contains(event.target)) {
            return
          }
          this.isTypeOpen = false
        }
        const popover = this.$refs.popover
        const card = this.$refs.card
        if (popover && !popover.contains(event.target) && card && !card.contains(event.target)) {
          this.closePopover(true)
        }
      }
      document.addEventListener('click', this.documentClickHandler)
    },
    // Close the radius popover and optionally clear the active row.
    closePopover(clearActive = false) {
      const card = this.$refs.card
      const popover = this.$refs.popover
      if (!card || !popover) {
        return
      }

      const triggers = card.querySelectorAll('[data-radius-trigger]')
      popover.classList.add('d-none')
      triggers.forEach((btn) => btn.classList.remove('is-active'))
      if (clearActive) {
        this.activeRowId = null
      }
    },
    // Open and position the radius popover for the selected row.
    openRadiusPopover(row, event) {
      const card = this.$refs.card
      const popover = this.$refs.popover
      if (!card || !popover) {
        return
      }

      if (this.activeRowId === row.id) {
        this.closePopover(true)
        return
      }
      this.activeRowId = row.id
      this.focusRowOnMap(row)

      const triggers = card.querySelectorAll('[data-radius-trigger]')
      triggers.forEach((btn) => btn.classList.remove('is-active'))

      const triggerEl = card.querySelector(`[data-radius-trigger][data-row-id=\"${row.id}\"]`)
      if (triggerEl) {
        triggerEl.classList.add('is-active')
      }

      popover.classList.remove('d-none')

      const triggerRect = (event?.currentTarget || triggerEl || card).getBoundingClientRect()
      const cardRect = card.getBoundingClientRect()
      const top = triggerRect.bottom - cardRect.top + 8
      const left = Math.min(
        triggerRect.left - cardRect.left,
        card.clientWidth - popover.offsetWidth - 16
      )

      popover.style.top = `${top}px`
      popover.style.left = `${Math.max(left, 16)}px`

    },
    // Center/fit the map to the selected row or its circle.
    focusRowOnMap(row) {
      if (!this.map || !row) {
        return
      }
      const circle = this.circles[row.id]
      if (circle) {
        this.map.fitBounds(circle.getBounds(), { padding: [30, 30] })
        return
      }
      if (typeof row.lat === 'number' && typeof row.lng === 'number') {
        this.map.setView([row.lat, row.lng], 12)
      }
    },
    // Return whether a row id belongs to include or exclude.
    getRowTypeById(rowId) {
      if (!rowId) {
        return null
      }
      const inInclude = this.currentRegion.include.find((row) => row.id === rowId)
      if (inInclude) {
        return 'include'
      }
      const inExclude = this.currentRegion.exclude.find((row) => row.id === rowId)
      if (inExclude) {
        return 'exclude'
      }
      return null
    },
    // Build a Leaflet marker icon based on type and active state.
    buildMarkerIcon(type, isActive) {
      const markerColor = isActive ? '#2563eb' : type === 'include' ? '#22c55e' : '#ef4444'
      const markerSymbol = isActive ? '+' : type === 'include' ? '✓' : '×'
      const markerSvg = `
        <svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M14 0C6.82 0 1 5.82 1 13c0 9.32 10.8 21.64 12.33 23.39.36.41.98.41 1.34 0C16.2 34.64 27 22.32 27 13 27 5.82 21.18 0 14 0z" fill="${markerColor}"/>
          <text x="14" y="18" text-anchor="middle" font-size="16" font-weight="700" fill="#ffffff">${markerSymbol}</text>
        </svg>
      `
      return L.divIcon({
        className: 'region-targeting-marker',
        html: markerSvg,
        iconSize: [28, 36],
        iconAnchor: [14, 34]
      })
    },
    // Update marker/circle styles when a row becomes active/inactive.
    updateRowStyles(rowId, isActive) {
      const type = this.getRowTypeById(rowId)
      if (!type) {
        return
      }
      const circle = this.circles[rowId]
      if (circle) {
        const color = isActive ? '#2563eb' : type === 'exclude' ? '#ef4444' : '#22c55e'
        circle.setStyle({ color, fillColor: color })
      }
      const marker = this.markersById[rowId]
      if (marker) {
        marker.setIcon(this.buildMarkerIcon(type, isActive))
      }
    },
    // Update radius from slider input.
    updateRadiusFromSlider(event) {
      if (!this.activeRow) {
        return
      }

      const value = Number(event.target.value)
      this.activeRow.radius = value
      this.updateCircleRadius(this.activeRow)
    },
    // Update radius from text input (clamped).
    updateRadiusFromInput(event) {
      if (!this.activeRow) {
        return
      }

      const rawValue = event.target.value.replace(/[^\d]/g, '')
      const value = Math.min(Math.max(Number(rawValue || 0), 10), 50)
      this.activeRow.radius = value
      event.target.value = value
      this.updateCircleRadius(this.activeRow)
    },
    // Format row metadata (street/county/country/postcode).
    formatRowLabel(row) {
      if (!row) {
        return ''
      }
      const streetLine = [row.street, row.house_number].filter(Boolean).join(' ')
      const cityLine = row.city || row.state || row.region || ''
      const parts = [streetLine, cityLine].filter(Boolean)
      if (parts.length > 0) {
        return parts.join(', ')
      }
      return row.label || ''
    },
    // Format row metadata (county/district/country/postcode).
    formatRowMeta(row) {
      if (!row) {
        return ''
      }
      const parts = []
      const area = row.county || row.district || ''
      if (area) {
        parts.push(area)
      }
      if (row.country) {
        parts.push(row.country)
      }
      if (row.postcode) {
        parts.push(row.postcode)
      }
      return parts.join(', ')
    },
    // Format search result metadata (street/house number/postcode/region/country).
    formatSearchLabel(result) {
      const props = result?.properties
      if (!props) {
        return ''
      }
      const streetLine = [props.street || props.address_line1, props.housenumber]
        .filter(Boolean)
        .join(' ')
      const cityLine = props.city || props.state || props.region || ''
      const parts = [streetLine, cityLine].filter(Boolean)
      if (parts.length > 0) {
        return parts.join(', ')
      }
      return props.formatted || props.name || props.label || ''
    },
    // Format search result metadata (county/district/country/postcode).
    formatSearchMeta(result) {
      const props = result?.properties
      if (!props) {
        return ''
      }
      const parts = []
      const area = props.county || props.district || props.suburb || props.municipality || ''
      if (area) {
        parts.push(area)
      }
      if (props.country) {
        parts.push(props.country)
      }
      if (props.postcode) {
        parts.push(props.postcode)
      }
      return parts.join(', ')
    },
    // Initialize the Leaflet map and bind click handlers.
    initMap() {
      if (!this.$refs.mapContainer) {
        return
      }

      delete L.Icon.Default.prototype._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: markerIcon2x,
        iconUrl: markerIcon,
        shadowUrl: markerShadow
      })

      this.map = L.map(this.$refs.mapContainer, { zoomControl: false }).setView([39.8283, -98.5795], 4)
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }).addTo(this.map)
      L.control.zoom({ position: 'topright' }).addTo(this.map)

      this.map.on('click', (event) => {
        if (!this.dropPinMode) {
          return
        }
        this.addLocationFromPin({ lat: event.latlng.lat, lng: event.latlng.lng })
      })

      // autocomplete powered by Geoapify
    },
    // Toggle drop-pin mode.
    toggleDropPin() {
      this.dropPinMode = !this.dropPinMode
    },
    // Toggle map collapse state.
    toggleMapCollapse() {
      this.isMapCollapsed = !this.isMapCollapsed
    },
    // Log Geoapify request counts/URLs for debugging.
    logGeoapifyRequest(context, url) {
      this.geoapifyRequestCount += 1
      // console.info(`[Geoapify #${this.geoapifyRequestCount}] ${context}`, { url })
    },
    // Log Geoapify response payloads/summaries for debugging.
    logGeoapifyResult(context, payload) {
      // console.info(`[Geoapify] ${context}`, payload)
    },
    // Log the serialized payload after mutations.
    logPayloadState(action) {
      // console.info(`[RegionLocations] payload after ${action}`, this.regionsData)
    },
    // Flatten a few Geoapify fields onto the row without duplicating core fields.
    applyGeoapifyExtras(row, props) {
      if (!row || !props || typeof props !== 'object') {
        return
      }
      const extras = {}
      if (props.place_id) {
        extras.place_id = props.place_id
      }
      if (props.plus_code) {
        extras.plus_code = props.plus_code
      }
      if (props.suburb) {
        extras.suburb = props.suburb
      }
      if (props.municipality) {
        extras.municipality = props.municipality
      }
      if (props.state_code) {
        extras.state_code = props.state_code
      }
      if (props.category) {
        extras.category = props.category
      }
      if (props.formatted && props.formatted !== row.label) {
        extras.formatted = props.formatted
      }
      Object.assign(row, extras)
    },
    async addLocationFromPin(latlng) {
      const label = `Pin (lat ${latlng.lat.toFixed(5)}, lng ${latlng.lng.toFixed(5)})`
      const type = this.getCurrentType()
      const row = {
        id: this.nextRowId++,
        label,
        radius: 10,
        lat: latlng.lat,
        lng: latlng.lng,
        street: null,
        house_number: null,
        country: null,
        city: null,
        state: null,
        region: null,
        county: null,
        district: null,
        postcode: null,
        bbox_min_lon: null,
        bbox_min_lat: null,
        bbox_max_lon: null,
        bbox_max_lat: null,
        source: 'pin'
      }
      // Useful debug info from Leaflet's LatLng object.
      // console.log('Dropped pin latlng:', {
      //   lat: latlng.lat,
      //   lng: latlng.lng,
      //   type: typeof latlng,
      //   raw: latlng
      // })
      this.regionsData[type].push(row)
      // console.log('Payload after pin add:', this.regionsData)
      this.addMarker(latlng, row.id, type)
      this.addRadiusCircle(row, type)
      this.logPayloadState('pin add')
      this.dropPinMode = false
      await this.reverseGeocodeRow(row)
    },
    async reverseGeocodeRow(row) {
      if (!row || typeof row.lat !== 'number' || typeof row.lng !== 'number') {
        return
      }
      if (!this.geoapifyKey) {
        // console.warn('Geoapify key missing; reverse geocoding skipped.')
        return
      }
      try {
        const url = `https://api.geoapify.com/v1/geocode/reverse?lat=${encodeURIComponent(
          row.lat
        )}&lon=${encodeURIComponent(row.lng)}&limit=1&format=geojson&apiKey=${encodeURIComponent(
          this.geoapifyKey
        )}`
        this.logGeoapifyRequest('reverse', url)
        const response = await fetch(url)
        if (!response.ok) {
          return
        }
        const data = await response.json()
        this.logGeoapifyResult('reverse response', data)
        const feature = data?.features?.[0]
        if (!feature || !feature.properties) {
          return
        }
        const props = feature.properties
        this.logGeoapifyResult('reverse properties', props)
        this.applyGeoapifyExtras(row, props)
        const resolvedName =
          props.formatted ||
          props.address_line1 ||
          props.name ||
          props.label ||
          row.label
        const coordSuffix = `(${row.lat.toFixed(5)}, ${row.lng.toFixed(5)})`
        if (resolvedName.includes(coordSuffix)) {
          row.label = resolvedName
        } else {
          row.label = `${resolvedName} ${coordSuffix}`
        }
        row.street = props.street || props.address_line1 || props.name || row.street
        row.house_number = props.housenumber || row.house_number
        row.country = props.country || row.country
        row.city = props.city || row.city
        row.state = props.state || row.state
        row.region = props.region || row.region
        row.county = props.county || props.state || props.region || row.county
        row.district = props.district || row.district
        row.postcode = props.postcode || row.postcode
        this.replaceRowInData(row)
        // console.log('Payload after reverse geocode:', this.regionsData)
      } catch (error) {
        // console.error('Reverse geocode failed:', error)
      }
    },
    // Replace a row in regionsData after updates.
    replaceRowInData(row) {
      if (!row || !row.id) {
        return
      }
      const includeIndex = this.currentRegion.include.findIndex((item) => item.id === row.id)
      if (includeIndex !== -1) {
        this.currentRegion.include.splice(includeIndex, 1, { ...row })
        return
      }
      const excludeIndex = this.currentRegion.exclude.findIndex((item) => item.id === row.id)
      if (excludeIndex !== -1) {
        this.currentRegion.exclude.splice(excludeIndex, 1, { ...row })
      }
    },
    // Convert a search feature into a row and add it.
    addLocationFromSearch(feature) {
      if (!feature || !feature.geometry || !feature.geometry.coordinates) {
        return
      }
      const [lng, lat] = feature.geometry.coordinates
      const props = feature.properties || {}
      const type = this.getCurrentType()
      const label =
        props.formatted ||
        props.address_line1 ||
        props.name ||
        props.label ||
        'Selected location'
      const isCountryResult = props.result_type === 'country'
      const featureBbox = (isCountryResult && Array.isArray(feature.bbox) && feature.bbox.length === 4)
        ? feature.bbox
        : (isCountryResult ? this.findCountryBbox(props.country || props.name) : null)
      const row = {
        id: this.nextRowId++,
        label,
        radius: 10,
        lat,
        lng,
        street: props.street || props.address_line1 || props.name || null,
        house_number: props.housenumber || null,
        country: props.country || null,
        city: props.city || null,
        state: props.state || null,
        region: props.region || null,
        county: props.county || null,
        district: props.district || null,
        postcode: props.postcode || null,
        bbox_min_lon: featureBbox ? featureBbox[0] : null,
        bbox_min_lat: featureBbox ? featureBbox[1] : null,
        bbox_max_lon: featureBbox ? featureBbox[2] : null,
        bbox_max_lat: featureBbox ? featureBbox[3] : null,
        source: 'search'
      }
      this.applyGeoapifyExtras(row, props)
      this.regionsData[type].push(row)
      // console.log('Payload after search add:', this.regionsData)
      this.addMarker({ lat, lng }, row.id, type)
      this.addRadiusCircle(row, type)
      this.logPayloadState('search add')
      this.searchQuery = ''
      this.searchResults = []
      this.isSearchFocused = false
    },
    // Add a marker and bind click-to-edit behavior.
    addMarker(latlng, rowId, type) {
      if (!this.map) {
        return
      }
      const marker = L.marker([latlng.lat, latlng.lng], {
        icon: this.buildMarkerIcon(type, false)
      }).addTo(this.map)
      if (rowId) {
        marker.on('click', () => {
          const rowType = this.getRowTypeById(rowId)
          if (!rowType) {
            return
          }
          const row =
            rowType === 'include'
              ? this.currentRegion.include.find((item) => item.id === rowId)
              : this.currentRegion.exclude.find((item) => item.id === rowId)
          if (row) {
            this.suppressOutsideClose = true
            this.focusRowOnMap(row)
            this.openRadiusPopover(row)
          }
        })
      }
      this.markers.push(marker)
      if (rowId) {
        this.markersById[rowId] = marker
      }
    },
    // Add a radius circle to the map (optionally fit bounds).
    addRadiusCircle(row, type, options = {}) {
      if (!this.map) {
        return
      }
      // Skip creating radius circles for cities and countries
      if (row.locationType === 'city' || row.locationType === 'country') {
        return
      }
      const color = type === 'exclude' ? '#ef4444' : '#22c55e'
      const radiusMeters = row.radius * 1609.34
      const circle = L.circle([row.lat, row.lng], {
        radius: radiusMeters,
        color,
        fillColor: color,
        fillOpacity: 0.28,
        weight: 1,
        opacity: 0.55
      }).addTo(this.map)
      this.circles[row.id] = circle
      if (options.fitBounds !== false) {
        this.map.fitBounds(circle.getBounds(), { padding: [30, 30] })
      }
    },
    // Update circle radius in meters.
    updateCircleRadius(row) {
      const circle = this.circles[row.id]
      if (!circle) {
        return
      }
      circle.setRadius(row.radius * 1609.34)
    },
    // Remove a row and its map overlays.
    removeRow(row, type) {
      if (!row || !type) {
        return
      }
      this.regionsData[type] = this.regionsData[type].filter((item) => item.id !== row.id)
      if (this.circles[row.id]) {
        this.map.removeLayer(this.circles[row.id])
        delete this.circles[row.id]
      }
      if (this.markersById[row.id]) {
        this.map.removeLayer(this.markersById[row.id])
        delete this.markersById[row.id]
      }
      if (this.activeRowId === row.id) {
        this.activeRowId = null
      }
    },
    // Return current include/exclude selection.
    getCurrentType() {
      return this.currentType === 'include' ? 'include' : 'exclude'
    },
    // Debounced search input handler (min 3 chars).
    handleSearchInput() {
      // Photon autocomplete entrypoint (OpenStreetMap).
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce)
      }
      const query = this.searchQuery.trim()
      if (query.length < 3) {
        this.searchResults = []
        this.isSearching = false
        return
      }
      // console.log('Photon search query:', query)
      this.searchDebounce = setTimeout(() => {
        this.fetchSearchResults(query)
      }, 300)
    },
    async fetchSearchResults(query) {
      // Geoapify autocomplete returning GeoJSON features.
      this.isSearching = true
      if (!this.geoapifyKey) {
        // console.warn('Geoapify key missing; search skipped.')
        this.searchResults = []
        this.isSearching = false
        return
      }
      // console.log('Geoapify fetching:', query)
      try {
        const url = `https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(
          query
        )}&limit=20&format=geojson&apiKey=${encodeURIComponent(this.geoapifyKey)}`
        this.logGeoapifyRequest('autocomplete', url)
        const response = await fetch(url)
        if (!response.ok) {
          this.searchResults = []
          return
        }
        const data = await response.json()
        this.logGeoapifyResult('autocomplete response', {
          query,
          count: Array.isArray(data.features) ? data.features.length : 0,
          sample: Array.isArray(data.features) ? data.features.slice(0, 3) : []
        })
        // console.log('Geoapify raw response:', data)
        this.searchResults = Array.isArray(data.features) ? data.features : []
        // console.log('Geoapify results:', this.searchResults)
        // console.log('Geoapify results count:', this.searchResults.length)
        // console.log('Geoapify results data:', this.searchResults)
      } catch (error) {
        // console.error('Geoapify fetch error:', error)
        this.searchResults = []
      } finally {
        this.isSearching = false
      }
    },
    // Handle selecting a search result.
    selectSearchResult(feature) {
      // Photon selection: convert feature -> our row model.
      if (!feature || !feature.geometry || !feature.geometry.coordinates) {
        return
      }
      // console.log('Photon selected feature:', feature)
      this.addLocationFromSearch(feature)
    },
    // Hide search results shortly after blur.
    handleSearchBlur() {
      setTimeout(() => {
        this.isSearchFocused = false
      }, 150)
    },
    // Split bulk input into entries.
    parseBulkPostalCodes() {
      return this.bulkText
        .split(/[\n,;]+/g)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0)
    },
    // Normalize postcode strings for comparison.
    normalizePostcode(value) {
      return String(value || '')
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '')
    },
    // Report bulk add summary via toastr or console.
    notifyBulkSummary(addedCount, failedCount) {
      const message = `Added ${addedCount}, failed ${failedCount}.`
      if (window?.toastr?.info) {
        window.toastr.info(message, { timeOut: 5000 })
      } else {
        // console.log(message)
      }
    },
    // Async delay helper.
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms))
    },
    // Toggle global loader element if present.
    toggleGlobalLoader(isVisible) {
      const loaderEl = document.getElementById('loaderEl')
      if (!loaderEl) {
        return
      }
      if (isVisible) {
        loaderEl.classList.remove('d-none')
      } else {
        loaderEl.classList.add('d-none')
      }
    },
    // Reset bulk modal fields.
    resetBulkModal() {
      this.bulkType = ''
      this.bulkText = ''
      this.bulkCountry = ''
    },
    // Open delete confirmation modal.
    openDeleteModal() {
      const modalEl = document.getElementById(this.deleteModalId)
      if (!modalEl || !window.bootstrap?.Modal) {
        return
      }
      const instance = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl)
      instance.show()
    },
    // Close delete confirmation modal.
    closeDeleteModal() {
      const modalEl = document.getElementById(this.deleteModalId)
      if (!modalEl || !window.bootstrap?.Modal) {
        return
      }
      const instance = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl)
      instance.hide()
    },
    // Stage a row for deletion and open confirmation modal.
    requestRemoveRow(row, type) {
      if (!row || !type) {
        return
      }
      this.pendingRemove = { row, type }
      this.openDeleteModal()
    },
    // Confirm removal of the staged row.
    confirmRemoveRow() {
      if (!this.pendingRemove) {
        return
      }
      const { row, type } = this.pendingRemove
      this.removeRow(row, type)
      this.pendingRemove = null
      this.closeDeleteModal()
    },
    // Close the bulk add modal.
    closeBulkModal() {
      const modalEl = document.getElementById(this.bulkModalId)
      if (!modalEl || !window.bootstrap?.Modal) {
        return
      }
      const instance = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl)
      instance.hide()
    },
    async handleBulkMatch() {
      if (!['postal_codes', 'cities', 'countries'].includes(this.bulkType)) {
        return
      }
      if (this.bulkRequiresCountry && !this.bulkCountry) {
        return
      }
      if (this.bulkType === 'postal_codes' && !this.bulkCountryRecord) {
        return
      }
      if (!this.geoapifyKey) {
        // console.warn('Geoapify key missing; bulk add skipped.')
        return
      }
      const codes = this.parseBulkPostalCodes().slice(0, 25)
      // console.log('Bulk postal codes parsed:', codes)
      if (codes.length === 0) {
        return
      }
      this.bulkIsProcessing = true
      this.toggleGlobalLoader(true)
      const type = this.getCurrentType()
      const targetCountryCode = this.bulkCountryRecord?.code
        ? String(this.bulkCountryRecord.code).toLowerCase()
        : ''
      let addedCount = 0
      let failedCount = 0
      try {
        for (let index = 0; index < codes.length; index += 1) {
          const code = codes[index]
          const isCity = this.bulkType === 'cities'
          const isCountry = this.bulkType === 'countries'
          const countryName = this.bulkCountryRecord?.name || ''
          const countrySuffix =
            (isCity || this.bulkType === 'postal_codes') && countryName ? ` ${countryName}` : ''
          const query = encodeURIComponent(`${code}${countrySuffix}`)
          const filterParts = []
          if (this.bulkType === 'postal_codes' && this.bulkCountryRecord) {
            filterParts.push(
              `rect:${this.bulkCountryRecord.bbox_min_lon},${this.bulkCountryRecord.bbox_min_lat},${this.bulkCountryRecord.bbox_max_lon},${this.bulkCountryRecord.bbox_max_lat}`
            )
          }
          if (targetCountryCode) {
            filterParts.push(`countrycode:${targetCountryCode}`)
          }
          const filterParam = filterParts.length > 0
            ? `&filter=${encodeURIComponent(filterParts.join('|'))}`
            : ''
          const limit = this.bulkType === 'postal_codes' ? 10 : 1
          const url = `https://api.geoapify.com/v1/geocode/search?text=${query}&limit=${limit}${filterParam}&format=geojson&apiKey=${encodeURIComponent(
            this.geoapifyKey
          )}`
          // console.log('Bulk location fetch:', { code, url })
          this.logGeoapifyRequest('bulk-search', url)
          const response = await fetch(url)
          if (!response.ok) {
            // console.warn('Bulk location fetch failed:', code, response.status)
            failedCount += 1
            continue
          }
          const data = await response.json()
          this.logGeoapifyResult('bulk-search response', {
            code,
            count: Array.isArray(data?.features) ? data.features.length : 0
          })
          let features = Array.isArray(data?.features) ? data.features : []
          let feature = features[0]
          if (this.bulkType === 'postal_codes') {
            const normalizedInput = this.normalizePostcode(code)
            features = features.filter((item) => {
              const itemCode = item?.properties?.country_code
              if (!itemCode || !targetCountryCode) {
                return false
              }
              return String(itemCode).toLowerCase() === targetCountryCode
            })
            feature =
              features.find(
                (item) =>
                  this.normalizePostcode(item?.properties?.postcode) === normalizedInput
              ) || 
              features[0]
          }
          if (!feature || !feature.geometry?.coordinates) {
            // console.warn('Bulk location no results, retrying without filters:', code)
            const fallbackUrl = `https://api.geoapify.com/v1/geocode/search?text=${query}&limit=${limit}${filterParam}&format=geojson&apiKey=${encodeURIComponent(
              this.geoapifyKey
            )}`
            this.logGeoapifyRequest('bulk-search-fallback', fallbackUrl)
            const fallbackResponse = await fetch(fallbackUrl)
            if (!fallbackResponse.ok) {
              // console.warn('Bulk location fallback failed:', code, fallbackResponse.status)
              failedCount += 1
              continue
            }
            const fallbackData = await fallbackResponse.json()
            this.logGeoapifyResult('bulk-search-fallback response', {
              code,
              count: Array.isArray(fallbackData?.features) ? fallbackData.features.length : 0
            })
            let fallbackFeatures = Array.isArray(fallbackData?.features) ? fallbackData.features : []
            let fallbackFeature = fallbackFeatures[0]
            if (this.bulkType === 'postal_codes') {
              const normalizedInput = this.normalizePostcode(code)
              fallbackFeatures = fallbackFeatures.filter((item) => {
                const itemCode = item?.properties?.country_code
                if (!itemCode || !targetCountryCode) {
                  return false
                }
                return String(itemCode).toLowerCase() === targetCountryCode
              })
              fallbackFeature =
                fallbackFeatures.find(
                  (item) =>
                    this.normalizePostcode(item?.properties?.postcode) === normalizedInput
                ) || fallbackFeatures[0]
            }
            if (!fallbackFeature || !fallbackFeature.geometry?.coordinates) {
              // console.warn('Bulk location no results after fallback:', code)
              failedCount += 1
              continue
            }
            const [fallbackLng, fallbackLat] = fallbackFeature.geometry.coordinates
            const fallbackBbox = (isCountry && Array.isArray(fallbackFeature.bbox) && fallbackFeature.bbox.length === 4)
              ? fallbackFeature.bbox
              : (isCountry ? this.findCountryBbox(fallbackFeature.properties?.country || code) : null)
            const fallbackRow = {
              id: this.nextRowId++,
              label:
                fallbackFeature.properties?.formatted ||
                fallbackFeature.properties?.address_line1 ||
                fallbackFeature.properties?.name ||
                code,
              radius: 10,
              lat: fallbackLat,
              lng: fallbackLng,
              street:
                fallbackFeature.properties?.street ||
                fallbackFeature.properties?.address_line1 ||
                fallbackFeature.properties?.name ||
                null,
              house_number: fallbackFeature.properties?.housenumber || null,
              country: fallbackFeature.properties?.country || null,
              city: fallbackFeature.properties?.city || null,
              state: fallbackFeature.properties?.state || null,
              region: fallbackFeature.properties?.region || null,
              county: fallbackFeature.properties?.county || null,
              district: fallbackFeature.properties?.district || null,
              postcode: fallbackFeature.properties?.postcode || null,
              bbox_min_lon: fallbackBbox ? fallbackBbox[0] : null,
              bbox_min_lat: fallbackBbox ? fallbackBbox[1] : null,
              bbox_max_lon: fallbackBbox ? fallbackBbox[2] : null,
              bbox_max_lat: fallbackBbox ? fallbackBbox[3] : null,
              source: 'bulk',
              locationType: this.bulkType === 'countries' ? 'country' : (this.bulkType === 'cities' ? 'city' : 'postal_code')
            }
            this.applyGeoapifyExtras(fallbackRow, fallbackFeature.properties)
            this.regionsData[type].push(fallbackRow)
            // console.log('Payload after bulk add (fallback):', this.regionsData)
            this.addMarker({ lat: fallbackLat, lng: fallbackLng }, fallbackRow.id, type)
            this.addRadiusCircle(fallbackRow, type)
            this.logPayloadState('bulk add (fallback)')
            addedCount += 1
            // console.log('Bulk postal code added (fallback):', {
            //   code,
            //   label: fallbackRow.label,
            //   lat: fallbackLat,
            //   lng: fallbackLng,
            //   type
            // })
            if (index < codes.length - 1) {
              await this.sleep(1000)
            }
            continue
          }
          const [lng, lat] = feature.geometry.coordinates
          const bulkBbox = (isCountry && Array.isArray(feature.bbox) && feature.bbox.length === 4)
            ? feature.bbox
            : (isCountry ? this.findCountryBbox(feature.properties?.country || code) : null)
          const row = {
            id: this.nextRowId++,
            label:
              feature.properties?.formatted ||
              feature.properties?.address_line1 ||
              feature.properties?.name ||
              code,
            radius: 10,
            lat,
            lng,
            street:
              feature.properties?.street ||
              feature.properties?.address_line1 ||
              feature.properties?.name ||
              null,
            house_number: feature.properties?.housenumber || null,
            country: feature.properties?.country || null,
            city: feature.properties?.city || null,
            state: feature.properties?.state || null,
            region: feature.properties?.region || null,
            county: feature.properties?.county || null,
            district: feature.properties?.district || null,
            postcode: feature.properties?.postcode || null,
            bbox_min_lon: bulkBbox ? bulkBbox[0] : null,
            bbox_min_lat: bulkBbox ? bulkBbox[1] : null,
            bbox_max_lon: bulkBbox ? bulkBbox[2] : null,
            bbox_max_lat: bulkBbox ? bulkBbox[3] : null,
            source: 'bulk',
            locationType: this.bulkType === 'countries' ? 'country' : (this.bulkType === 'cities' ? 'city' : 'postal_code')
          }
          this.applyGeoapifyExtras(row, feature.properties)
          this.regionsData[type].push(row)
          // console.log('Payload after bulk add:', this.regionsData)
          this.addMarker({ lat, lng }, row.id, type)
          this.addRadiusCircle(row, type)
          this.logPayloadState('bulk add')
          addedCount += 1
          // console.log('Bulk postal code added:', {
          //   code,
          //   label: row.label,
          //   lat,
          //   lng,
          //   type
          // })
          if (index < codes.length - 1) {
            await this.sleep(1000)
          }
        }
      } catch (error) {
        // console.error('Bulk add failed:', error)
      } finally {
        this.bulkIsProcessing = false
        this.toggleGlobalLoader(false)
        this.notifyBulkSummary(addedCount, failedCount)
        this.resetBulkModal()
        this.closeBulkModal()
      }
    },
    // Look up bbox from the countries prop (populated from DB with bbox columns).
    findCountryBbox(countryName) {
      if (!countryName || !Array.isArray(this.countries)) {
        return null
      }
      const name = countryName.trim().toLowerCase()
      const match = this.countries.find(
        (c) => c.name && c.name.trim().toLowerCase() === name
      )
      if (
        match &&
        match.bbox_min_lon != null &&
        match.bbox_min_lat != null &&
        match.bbox_max_lon != null &&
        match.bbox_max_lat != null
      ) {
        return [
          parseFloat(match.bbox_min_lon),
          parseFloat(match.bbox_min_lat),
          parseFloat(match.bbox_max_lon),
          parseFloat(match.bbox_max_lat)
        ]
      }
      return null
    }
  }
}
</script>
